import React, { useState, useEffect, useCallback } from "react";
import { prospectGroups as api, prospects as prospectsApi } from "../services/api";
import toast from "react-hot-toast";
import {
  FiPlus, FiTrash2, FiEdit2, FiUsers, FiSearch,
  FiX, FiUserPlus, FiUserMinus, FiChevronLeft, FiRefreshCw,
  FiFilter, FiHash, FiTag
} from "react-icons/fi";

const COLORS = ["#8b5cf6","#10b981","#f59e0b","#ef4444","#3b82f6","#ec4899","#06b6d4","#f97316"];

// Filter types
const FILTER_TYPES = [
  { id: "manual",          label: "يدوي (اختيار فردي)",        icon: FiUserPlus, desc: "اختر الأعضاء يدوياً بعد إنشاء المجموعة" },
  { id: "wa_group_name",   label: "حسب اسم جروب واتساب",      icon: FiTag,      desc: "كل محتمل مشترك في جروب واتساب معين" },
  { id: "min_group_count", label: "حسب عدد الجروبات المشترك",  icon: FiHash,     desc: "كل محتمل مشترك في عدد معين من الجروبات أو أكثر" },
  { id: "combined",        label: "مدمج (اسم + عدد)",           icon: FiFilter,   desc: "مشترك في الجروب المحدد AND لديه عدد جروبات ≥ X" },
];

export default function ProspectGroupsPage() {
  const [groups, setGroups]       = useState([]);
  const [selected, setSelected]   = useState(null); // group object
  const [members, setMembers]     = useState([]);
  const [allProspects, setAllProspects] = useState([]);
  const [waGroupNames, setWaGroupNames] = useState([]); // [{name, count}]
  const [loading, setLoading]     = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showAdd, setShowAdd]     = useState(false);

  // New/edit form state
  const [editId, setEditId]       = useState(null);
  const [newName, setNewName]     = useState("");
  const [newDesc, setNewDesc]     = useState("");
  const [newColor, setNewColor]   = useState("#8b5cf6");
  const [newFilterType, setNewFilterType] = useState("manual");
  const [newWaGroupName, setNewWaGroupName] = useState("");
  const [newMinCount, setNewMinCount] = useState(2);

  const [search, setSearch]       = useState("");
  const [addSearch, setAddSearch] = useState("");

  // ─── Loaders ───
  const loadGroups = useCallback(async () => {
    try {
      const res = await api.list();
      setGroups(res.data?.groups || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  const loadMembers = useCallback(async (groupId) => {
    try {
      const res = await api.getMembers(groupId);
      setMembers(res.data?.members || []);
    } catch {}
  }, []);

  const loadAllProspects = useCallback(async () => {
    try {
      const res = await prospectsApi.list();
      setAllProspects(res.data?.prospects || res.data?.items || res.data || []);
    } catch {}
  }, []);

  const loadWaGroupNames = useCallback(async () => {
    try {
      const res = await api.getWaGroupNames();
      setWaGroupNames(res.data?.groups || []);
    } catch {}
  }, []);

  useEffect(() => {
    if (selected) loadMembers(selected.id);
  }, [selected, loadMembers]);

  // ─── Actions ───
  const selectGroup = (g) => {
    setSelected(g);
    setShowAdd(false);
  };

  const resetForm = () => {
    setEditId(null);
    setNewName("");
    setNewDesc("");
    setNewColor("#8b5cf6");
    setNewFilterType("manual");
    setNewWaGroupName("");
    setNewMinCount(2);
  };

  const openCreate = () => {
    resetForm();
    setShowCreate(true);
    loadWaGroupNames();
  };

  const createGroup = async () => {
    if (!newName.trim()) return toast.error("اسم المجموعة مطلوب");
    if (newFilterType === "wa_group_name" && !newWaGroupName) {
      return toast.error("اختر اسم جروب واتساب");
    }
    if (newFilterType === "combined" && !newWaGroupName) {
      return toast.error("اختر اسم جروب واتساب");
    }
    try {
      const payload = {
        name: newName.trim(),
        description: newDesc.trim() || null,
        color: newColor,
        filter_type: newFilterType,
        filter_wa_group_name: (newFilterType === "wa_group_name" || newFilterType === "combined") ? newWaGroupName : null,
        filter_min_group_count: (newFilterType === "min_group_count" || newFilterType === "combined") ? Number(newMinCount) : null,
      };
      const res = await api.create(payload);
      const count = res.data?.member_count || 0;
      toast.success(
        newFilterType === "manual"
          ? "تم إنشاء المجموعة ✓"
          : `تم إنشاء المجموعة — ${count} عضو مضاف تلقائياً ✓`
      );
      resetForm();
      setShowCreate(false);
      loadGroups();
    } catch (e) {
      toast.error(e.response?.data?.error || "فشل الإنشاء");
    }
  };

  const updateGroup = async () => {
    if (!editId || !newName.trim()) return;
    try {
      await api.update(editId, { name: newName.trim(), description: newDesc.trim() || null, color: newColor });
      toast.success("تم التعديل ✓");
      resetForm();
      setShowCreate(false);
      loadGroups();
      if (selected?.id === editId) setSelected(prev => ({ ...prev, name: newName.trim(), color: newColor }));
    } catch (e) {
      toast.error(e.response?.data?.error || "فشل التعديل");
    }
  };

  const startEdit = (g) => {
    setEditId(g.id);
    setNewName(g.name);
    setNewDesc(g.description || "");
    setNewColor(g.color || "#8b5cf6");
    setNewFilterType(g.filter_type || "manual");
    setNewWaGroupName(g.filter_wa_group_name || "");
    setNewMinCount(g.filter_min_group_count || 2);
    setShowCreate(true);
  };

  const deleteGroup = async (id) => {
    if (!window.confirm("حذف المجموعة؟")) return;
    try {
      await api.delete(id);
      toast.success("تم الحذف ✓");
      if (selected?.id === id) { setSelected(null); setMembers([]); }
      loadGroups();
    } catch {
      toast.error("فشل الحذف");
    }
  };

  const removeMember = async (prospectId) => {
    if (!selected) return;
    try {
      await api.removeMember(selected.id, prospectId);
      setMembers(prev => prev.filter(m => m.id !== prospectId));
      loadGroups();
    } catch {}
  };

  const openAddDialog = () => {
    loadAllProspects();
    setShowAdd(true);
    setAddSearch("");
  };

  const addProspects = async (prospectIds) => {
    if (!selected || !prospectIds.length) return;
    try {
      await api.addMembers(selected.id, prospectIds);
      toast.success(`تمت إضافة ${prospectIds.length} محتمل ✓`);
      loadMembers(selected.id);
      loadGroups();
      setShowAdd(false);
    } catch {
      toast.error("فشلت الإضافة");
    }
  };

  const resyncGroup = async () => {
    if (!selected) return;
    if (!window.confirm("إعادة مزامنة المجموعة؟\nسيتم إعادة تطبيق الفلتر وتحديث الأعضاء تلقائياً.")) return;
    try {
      const res = await api.resync(selected.id);
      toast.success(`تمت المزامنة — ${res.data.member_count} عضو`);
      loadMembers(selected.id);
      loadGroups();
    } catch (e) {
      toast.error(e.response?.data?.error || "فشلت المزامنة");
    }
  };

  const memberIds = new Set(members.map(m => m.id));
  const availableProspects = allProspects.filter(p =>
    !memberIds.has(p.id) &&
    (!addSearch || (p.name||"").includes(addSearch) || (p.phone||"").includes(addSearch))
  );

  const isFilterBased = selected && selected.filter_type && selected.filter_type !== "manual";

  return (
    <div style={{ height:"100vh", display:"flex", background:"#f8fafc" }}>
      {/* ── القائمة الجانبية: المجموعات ── */}
      <div style={{ width: selected ? 280 : "100%", maxWidth: 360, borderLeft:"1px solid #e2e8f0", display:"flex", flexDirection:"column", background:"#fff", flexShrink:0 }}>
        {/* Header */}
        <div style={{ padding:"16px 18px", borderBottom:"1px solid #e2e8f0", display:"flex", alignItems:"center", gap:10 }}>
          <FiUsers size={20} color="#8b5cf6"/>
          <span style={{ fontWeight:700, fontSize:17, color:"#1e293b", flex:1 }}>مجموعات المحتملين</span>
          <button onClick={openCreate}
            style={{ background:"#8b5cf6", border:"none", borderRadius:8, padding:"6px 12px", color:"#fff", cursor:"pointer", fontSize:12, fontFamily:"inherit", display:"flex", alignItems:"center", gap:4 }}>
            <FiPlus size={14}/> جديد
          </button>
        </div>

        {/* Groups list */}
        <div style={{ flex:1, overflowY:"auto" }}>
          {loading ? (
            <div style={{ padding:30, textAlign:"center", color:"#94a3b8", fontSize:13 }}>جاري التحميل...</div>
          ) : groups.length === 0 ? (
            <div style={{ padding:40, textAlign:"center", color:"#94a3b8" }}>
              <FiUsers size={40} style={{ opacity:0.3, marginBottom:10 }}/>
              <div style={{ fontSize:14, marginBottom:6 }}>لا توجد مجموعات</div>
              <div style={{ fontSize:12 }}>أنشئ مجموعة لتصنيف المحتملين</div>
            </div>
          ) : groups.map(g => {
            const ft = FILTER_TYPES.find(f => f.id === g.filter_type) || FILTER_TYPES[0];
            return (
              <div key={g.id} onClick={() => selectGroup(g)}
                style={{
                  padding:"12px 16px", cursor:"pointer", borderBottom:"1px solid #f8fafc",
                  background: selected?.id === g.id ? "#eff6ff" : "transparent",
                  borderRight: selected?.id === g.id ? `3px solid ${g.color||"#8b5cf6"}` : "3px solid transparent",
                  display:"flex", alignItems:"center", gap:12, transition:"background 0.15s"
                }}>
                <div style={{
                  width:38, height:38, borderRadius:"50%", flexShrink:0,
                  background: g.color || "#8b5cf6",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  color:"#fff", fontWeight:700, fontSize:14
                }}>
                  <FiUsers size={16}/>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:14, color:"#1e293b", display:"flex", alignItems:"center", gap:6 }}>
                    {g.name}
                    {g.filter_type !== "manual" && (
                      <span title={ft.label} style={{ fontSize:10, background:"#ede9fe", color:"#7c3aed", padding:"1px 6px", borderRadius:8, fontWeight:600 }}>
                        فلتر
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize:12, color:"#94a3b8" }}>{g.member_count} محتمل</div>
                </div>
                <div style={{ display:"flex", gap:4 }}>
                  <button onClick={e => { e.stopPropagation(); startEdit(g); }}
                    style={{ background:"none", border:"none", cursor:"pointer", color:"#94a3b8", padding:4 }}>
                    <FiEdit2 size={13}/>
                  </button>
                  <button onClick={e => { e.stopPropagation(); deleteGroup(g.id); }}
                    style={{ background:"none", border:"none", cursor:"pointer", color:"#ef4444", padding:4 }}>
                    <FiTrash2 size={13}/>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── المحتوى: أعضاء المجموعة ── */}
      {selected && (
        <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
          {/* Header */}
          <div style={{ padding:"14px 20px", borderBottom:"1px solid #e2e8f0", background:"#fff", display:"flex", alignItems:"center", gap:12 }}>
            <button onClick={() => setSelected(null)}
              style={{ background:"none", border:"none", cursor:"pointer", color:"#94a3b8", padding:4, display:"none" }}>
              <FiChevronLeft size={20}/>
            </button>
            <div style={{ width:36, height:36, borderRadius:"50%", background: selected.color||"#8b5cf6", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff" }}>
              <FiUsers size={16}/>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:700, fontSize:16, color:"#1e293b" }}>{selected.name}</div>
              <div style={{ fontSize:12, color:"#94a3b8" }}>
                {members.length} محتمل في المجموعة
                {selected.description && <span> — {selected.description}</span>}
              </div>
            </div>
            {isFilterBased && (
              <button onClick={resyncGroup}
                style={{ background:"#fef3c7", border:"1px solid #fcd34d", borderRadius:8, padding:"7px 14px", color:"#92400e", cursor:"pointer", fontSize:13, fontFamily:"inherit", display:"flex", alignItems:"center", gap:6 }}>
                <FiRefreshCw size={14}/> إعادة مزامنة
              </button>
            )}
            <button onClick={openAddDialog}
              style={{ background:"#8b5cf6", border:"none", borderRadius:8, padding:"7px 14px", color:"#fff", cursor:"pointer", fontSize:13, fontFamily:"inherit", display:"flex", alignItems:"center", gap:6 }}>
              <FiUserPlus size={14}/> إضافة محتملين
            </button>
          </div>

          {/* Filter info banner */}
          {isFilterBased && (
            <div style={{ padding:"10px 20px", background:"#f5f3ff", borderBottom:"1px solid #e9d5ff", fontSize:12, color:"#6d28d9", display:"flex", alignItems:"center", gap:8 }}>
              <FiFilter size={14}/>
              <span>
                مجموعة بفلتر:
                {selected.filter_type === "wa_group_name" && ` مشترك في جروب "${selected.filter_wa_group_name}"`}
                {selected.filter_type === "min_group_count" && ` مشترك في ${selected.filter_min_group_count}+ جروبات`}
                {selected.filter_type === "combined" && ` جروب "${selected.filter_wa_group_name}" + ${selected.filter_min_group_count}+ جروبات`}
              </span>
            </div>
          )}

          {/* Search */}
          <div style={{ padding:"10px 20px", background:"#fff", borderBottom:"1px solid #f1f5f9" }}>
            <div style={{ position:"relative" }}>
              <FiSearch size={14} style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", color:"#94a3b8" }}/>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="بحث في الأعضاء..."
                style={{ width:"100%", padding:"8px 32px 8px 12px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" }}/>
            </div>
          </div>

          {/* Members list */}
          <div style={{ flex:1, overflowY:"auto", background:"#f8fafc" }}>
            {members.filter(m => !search || (m.name||"").includes(search) || (m.phone||"").includes(search) || (m.nb_names||"").includes(search)).map(m => {
              // v3.3.600 — nb_names stored pipe-separated (up to 5 NumberBook names)
              const nbNames = (m.nb_names || "").split("|").map(s => s.trim()).filter(Boolean);
              return (
              <div key={m.id} style={{
                padding:"11px 20px", display:"flex", alignItems:"flex-start", gap:12,
                borderBottom:"1px solid #f1f5f9", background:"#fff"
              }}>
                <div style={{
                  width:36, height:36, borderRadius:"50%", background:"#e2e8f0", flexShrink:0,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontWeight:700, fontSize:14, color:"#64748b", marginTop:2
                }}>{(m.name||"?")[0] || "?"}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:500, fontSize:14, color:"#1e293b" }}>{m.name || "بدون اسم"}</div>
                  <div style={{ fontSize:12, color:"#94a3b8", direction:"ltr", textAlign:"right" }}>
                    {m.phone ? `+${m.phone}` : "بدون رقم"}
                    {m.group_count > 0 && <span style={{ marginRight:8, color:"#7c3aed" }}>• {m.group_count} جروب</span>}
                  </div>
                  {/* v3.3.600 — NumberBook names + notes */}
                  {nbNames.length > 0 && (
                    <div style={{ marginTop:5, display:"flex", flexWrap:"wrap", gap:4, alignItems:"center" }}>
                      <span style={{ fontSize:10, color:"#64748b", fontWeight:600 }}>📒 نمبر بوك:</span>
                      {nbNames.map((nm, i) => (
                        <span key={i} style={{ fontSize:11, background:"#eff6ff", color:"#1d4ed8", padding:"1px 7px", borderRadius:10 }}>{nm}</span>
                      ))}
                    </div>
                  )}
                  {m.notes && (
                    <div style={{ marginTop:4, fontSize:11, color:"#475569", background:"#fffbeb", border:"1px solid #fde68a", borderRadius:6, padding:"4px 8px", whiteSpace:"pre-wrap", wordBreak:"break-word" }}>
                      📝 {m.notes}
                    </div>
                  )}
                </div>
                <button onClick={() => removeMember(m.id)}
                  style={{ background:"#fee2e2", border:"none", borderRadius:6, padding:"5px 10px", cursor:"pointer", color:"#dc2626", fontSize:12, fontFamily:"inherit", display:"flex", alignItems:"center", gap:4, flexShrink:0 }}>
                  <FiUserMinus size={12}/> حذف
                </button>
              </div>
              );
            })}
            {members.length === 0 && (
              <div style={{ padding:40, textAlign:"center", color:"#94a3b8" }}>
                <FiUsers size={36} style={{ opacity:0.3, marginBottom:8 }}/>
                <div style={{ fontSize:14 }}>لا يوجد أعضاء</div>
                <div style={{ fontSize:12 }}>
                  {isFilterBased ? "اضغط 'إعادة مزامنة' لتطبيق الفلتر" : "اضغط 'إضافة محتملين' لإضافة أعضاء"}
                </div>
              </div>
            )}
          </div>

          {/* Add members dialog */}
          {showAdd && (
            <AddMembersDialog
              prospects={availableProspects}
              search={addSearch}
              onSearch={setAddSearch}
              onAdd={addProspects}
              onClose={() => setShowAdd(false)}
            />
          )}
        </div>
      )}

      {/* Create/Edit dialog */}
      {showCreate && (
        <CreateGroupDialog
          editMode={!!editId}
          name={newName}        setName={setNewName}
          desc={newDesc}        setDesc={setNewDesc}
          color={newColor}      setColor={setNewColor}
          filterType={newFilterType}  setFilterType={setNewFilterType}
          waGroupName={newWaGroupName} setWaGroupName={setNewWaGroupName}
          minCount={newMinCount}       setMinCount={setNewMinCount}
          waGroupNames={waGroupNames}
          onSubmit={editId ? updateGroup : createGroup}
          onClose={() => { setShowCreate(false); resetForm(); }}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Create/Edit Group Dialog — with filter-type selection
// ══════════════════════════════════════════════════════════════
function CreateGroupDialog({
  editMode, name, setName, desc, setDesc, color, setColor,
  filterType, setFilterType, waGroupName, setWaGroupName,
  minCount, setMinCount, waGroupNames, onSubmit, onClose,
}) {
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ background:"#fff", borderRadius:16, width:"min(92vw,560px)", maxHeight:"90vh", display:"flex", flexDirection:"column", boxShadow:"0 20px 40px rgba(0,0,0,0.2)" }}>
        {/* Header */}
        <div style={{ padding:"14px 18px", borderBottom:"1px solid #e2e8f0", display:"flex", alignItems:"center", gap:10 }}>
          <FiUsers size={18} color="#8b5cf6"/>
          <span style={{ fontWeight:700, fontSize:15, flex:1 }}>
            {editMode ? "تعديل المجموعة" : "إنشاء مجموعة محتملين جديدة"}
          </span>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"#94a3b8" }}>
            <FiX size={18}/>
          </button>
        </div>

        {/* Body — scrollable */}
        <div style={{ padding:"14px 18px", overflowY:"auto", flex:1 }}>
          {/* Name */}
          <label style={{ fontSize:12, fontWeight:600, color:"#475569", display:"block", marginBottom:4 }}>اسم المجموعة *</label>
          <input value={name} onChange={e=>setName(e.target.value)} placeholder="مثال: مهتمين بـ مشروع النهضة"
            style={{ width:"100%", padding:"9px 12px", border:"1px solid #cbd5e1", borderRadius:8, fontSize:14, fontFamily:"inherit", boxSizing:"border-box", marginBottom:12 }}/>

          {/* Description */}
          <label style={{ fontSize:12, fontWeight:600, color:"#475569", display:"block", marginBottom:4 }}>وصف (اختياري)</label>
          <input value={desc} onChange={e=>setDesc(e.target.value)} placeholder="وصف قصير..."
            style={{ width:"100%", padding:"9px 12px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:13, fontFamily:"inherit", boxSizing:"border-box", marginBottom:12 }}/>

          {/* Color */}
          <label style={{ fontSize:12, fontWeight:600, color:"#475569", display:"block", marginBottom:6 }}>اللون</label>
          <div style={{ display:"flex", gap:6, marginBottom:14 }}>
            {COLORS.map(c => (
              <div key={c} onClick={() => setColor(c)}
                style={{ width:28, height:28, borderRadius:"50%", background:c, cursor:"pointer", border: color===c?"3px solid #1e293b":"3px solid transparent" }}/>
            ))}
          </div>

          {/* Filter type (disabled in edit mode — filter is immutable after creation) */}
          {!editMode && (
            <>
              <label style={{ fontSize:12, fontWeight:600, color:"#475569", display:"block", marginBottom:6 }}>
                طريقة إنشاء المجموعة *
              </label>
              <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:14 }}>
                {FILTER_TYPES.map(ft => {
                  const Icon = ft.icon;
                  const active = filterType === ft.id;
                  return (
                    <div key={ft.id} onClick={() => setFilterType(ft.id)}
                      style={{
                        padding:"10px 12px", border: active?"2px solid #8b5cf6":"1px solid #e2e8f0",
                        borderRadius:10, cursor:"pointer",
                        background: active?"#f5f3ff":"#fff",
                        display:"flex", alignItems:"flex-start", gap:10
                      }}>
                      <div style={{ color: active?"#7c3aed":"#94a3b8", marginTop:2 }}>
                        <Icon size={16}/>
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13, fontWeight:600, color: active?"#5b21b6":"#1e293b" }}>{ft.label}</div>
                        <div style={{ fontSize:11, color:"#64748b", marginTop:2 }}>{ft.desc}</div>
                      </div>
                      <input type="radio" checked={active} readOnly
                        style={{ marginTop:4, accentColor:"#8b5cf6" }}/>
                    </div>
                  );
                })}
              </div>

              {/* WA Group name selector */}
              {(filterType === "wa_group_name" || filterType === "combined") && (
                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:12, fontWeight:600, color:"#475569", display:"block", marginBottom:4 }}>
                    اسم جروب الواتساب *
                  </label>
                  {waGroupNames.length === 0 ? (
                    <div style={{ padding:"10px 12px", background:"#fef3c7", border:"1px solid #fcd34d", borderRadius:8, fontSize:12, color:"#92400e" }}>
                      ⚠️ لا توجد أسماء جروبات مسجلة في المحتملين حالياً.
                      تأكد من استيراد محتملين من جروبات واتساب أولاً.
                    </div>
                  ) : (
                    <select value={waGroupName} onChange={e => setWaGroupName(e.target.value)}
                      style={{ width:"100%", padding:"9px 12px", border:"1px solid #cbd5e1", borderRadius:8, fontSize:13, fontFamily:"inherit", boxSizing:"border-box", background:"#fff" }}>
                      <option value="">— اختر جروب —</option>
                      {waGroupNames.map(g => (
                        <option key={g.name} value={g.name}>
                          {g.name} ({g.count} محتمل)
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* Min group count input */}
              {(filterType === "min_group_count" || filterType === "combined") && (
                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:12, fontWeight:600, color:"#475569", display:"block", marginBottom:4 }}>
                    الحد الأدنى لعدد الجروبات *
                  </label>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <input type="number" min={1} max={100} value={minCount}
                      onChange={e => setMinCount(Math.max(1, parseInt(e.target.value) || 1))}
                      style={{ width:100, padding:"9px 12px", border:"1px solid #cbd5e1", borderRadius:8, fontSize:14, fontFamily:"inherit", textAlign:"center", boxSizing:"border-box" }}/>
                    <span style={{ fontSize:12, color:"#64748b" }}>
                      سيتم ضم كل محتمل مشترك في {minCount} جروب أو أكثر
                    </span>
                  </div>
                </div>
              )}
            </>
          )}

          {editMode && (
            <div style={{ padding:"10px 12px", background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:8, fontSize:12, color:"#64748b" }}>
              ℹ️ لا يمكن تعديل إعدادات الفلتر بعد الإنشاء. لتغييرها، احذف المجموعة وأنشئها من جديد.
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:"12px 18px", borderTop:"1px solid #e2e8f0", display:"flex", gap:8 }}>
          <button onClick={onSubmit}
            style={{ flex:1, padding:"10px", background:"#8b5cf6", border:"none", borderRadius:8, color:"#fff", cursor:"pointer", fontSize:14, fontWeight:600, fontFamily:"inherit" }}>
            {editMode ? "حفظ التعديلات" : "إنشاء المجموعة"}
          </button>
          <button onClick={onClose}
            style={{ padding:"10px 18px", background:"#f1f5f9", border:"none", borderRadius:8, cursor:"pointer", fontSize:14, fontFamily:"inherit" }}>
            إلغاء
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Add Members Dialog (manual selection)
// ══════════════════════════════════════════════════════════════
function AddMembersDialog({ prospects, search, onSearch, onAdd, onClose }) {
  const [checked, setChecked] = useState(new Set());

  const toggle = (id) => setChecked(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const toggleAll = () => {
    setChecked(checked.size === prospects.length ? new Set() : new Set(prospects.map(p => p.id)));
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ background:"#fff", borderRadius:16, width:"min(90vw,500px)", maxHeight:"80vh", display:"flex", flexDirection:"column", boxShadow:"0 20px 40px rgba(0,0,0,0.2)" }}>
        <div style={{ padding:"14px 18px", borderBottom:"1px solid #e2e8f0", display:"flex", alignItems:"center", gap:10 }}>
          <FiUserPlus size={18} color="#8b5cf6"/>
          <span style={{ fontWeight:700, fontSize:15, flex:1 }}>إضافة محتملين للمجموعة</span>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"#94a3b8" }}><FiX size={18}/></button>
        </div>
        <div style={{ padding:"10px 18px", borderBottom:"1px solid #f1f5f9", display:"flex", gap:8, alignItems:"center" }}>
          <FiSearch size={14} color="#94a3b8"/>
          <input value={search} onChange={e=>onSearch(e.target.value)} placeholder="بحث..."
            style={{ flex:1, border:"none", outline:"none", fontSize:14, fontFamily:"inherit" }}/>
          <button onClick={toggleAll} style={{ fontSize:12, background:"#f1f5f9", border:"none", borderRadius:6, padding:"4px 10px", cursor:"pointer", fontFamily:"inherit" }}>
            {checked.size === prospects.length && prospects.length > 0 ? "إلغاء الكل" : "تحديد الكل"}
          </button>
          <span style={{ fontSize:12, background:"#ede9fe", color:"#7c3aed", padding:"2px 8px", borderRadius:10, fontWeight:600 }}>{checked.size}</span>
        </div>
        <div style={{ flex:1, overflowY:"auto", maxHeight:400 }}>
          {prospects.length === 0 ? (
            <div style={{ padding:30, textAlign:"center", color:"#94a3b8", fontSize:13 }}>لا يوجد محتملين متاحين</div>
          ) : prospects.map(p => (
            <label key={p.id} style={{
              display:"flex", alignItems:"center", gap:10, padding:"9px 18px",
              borderBottom:"1px solid #f8fafc", cursor:"pointer",
              background: checked.has(p.id) ? "#f5f3ff" : "transparent"
            }}>
              <input type="checkbox" checked={checked.has(p.id)} onChange={() => toggle(p.id)}
                style={{ width:15, height:15, accentColor:"#8b5cf6" }}/>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:500 }}>{p.name || "بدون اسم"}</div>
                <div style={{ fontSize:11, color:"#94a3b8", direction:"ltr", textAlign:"right" }}>
                  {p.phone || "بدون رقم"}
                  {p.group_count > 0 && <span style={{ color:"#7c3aed", marginRight:6 }}>• {p.group_count} جروب</span>}
                </div>
              </div>
            </label>
          ))}
        </div>
        <div style={{ padding:"12px 18px", borderTop:"1px solid #e2e8f0" }}>
          <button onClick={() => onAdd([...checked])} disabled={checked.size === 0}
            style={{
              width:"100%", padding:"10px", background: checked.size > 0 ? "#8b5cf6" : "#94a3b8",
              border:"none", borderRadius:8, color:"#fff", cursor:"pointer", fontSize:14, fontWeight:600, fontFamily:"inherit"
            }}>
            إضافة {checked.size} محتمل
          </button>
        </div>
      </div>
    </div>
  );
}
